import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LayoutDashboard, Package, Heart, Star, Bell, Settings, Link2, LifeBuoy, PlusSquare, ClipboardList, DollarSign, Wallet, Store, Users, FileCheck, Tags, ShoppingBag, AlertOctagon, Landmark, MessageSquare, SlidersHorizontal, ScrollText, TicketPercent, Megaphone, Bitcoin, ShieldCheck, UserCog, Webhook, BadgeCheck } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { DashboardLayout, RequireAuth } from "@/components/layout/DashboardLayout";
import Home from "@/pages/Home";
import Marketplace from "@/pages/Marketplace";
import ProductPage from "@/pages/ProductPage";
import { CategoriesPage, StorePage, PromoPage } from "@/pages/Browse";
import { CartPage, PaymentSuccess, PaymentCancel } from "@/pages/Cart";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "@/pages/Auth";
import { BecomeSellerPage, SupportPage, LegalPage } from "@/pages/Info";
import * as A from "@/pages/Account";
import * as S from "@/pages/Seller";
import * as AD from "@/pages/Admin";
import { AdminDiscounts, AdminPromotions } from "@/pages/Marketing";
import { CryptoPaymentPage, AdminCryptoPayments } from "@/pages/Crypto";
import { AdminStaffRoles, AdminStaffAccounts, StaffLoginPage } from "@/pages/StaffRoles";
import { AdminOfficialStore, AdminWebhooks } from "@/pages/Official";

const ACCOUNT_NAV = [
  { to: "/account", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/account/orders", label: "My Orders", icon: Package },
  { to: "/account/wishlist", label: "Wishlist", icon: Heart },
  { to: "/account/reviews", label: "Reviews", icon: Star },
  { to: "/account/notifications", label: "Notifications", icon: Bell },
  { to: "/account/settings", label: "Account Settings", icon: Settings },
  { to: "/account/linked", label: "Linked Accounts", icon: Link2 },
  { to: "/support", label: "Support", icon: LifeBuoy },
];
const SELLER_NAV = [
  { to: "/seller", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/seller/products", label: "My Products", icon: Package, end: true },
  { to: "/seller/products/new", label: "Add Product", icon: PlusSquare },
  { to: "/seller/orders", label: "Orders", icon: ClipboardList },
  { to: "/seller/earnings", label: "Earnings", icon: DollarSign },
  { to: "/seller/withdrawals", label: "Withdrawals", icon: Wallet },
  { to: "/seller/reviews", label: "Reviews", icon: Star },
  { to: "/seller/store", label: "Store Settings", icon: Store },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, perm: "dashboard.view" },
  { section: "Customers" },
  { to: "/admin/users", label: "Users", icon: Users, perm: "users.view" },
  { section: "Sellers" },
  { to: "/admin/applications", label: "Applications", icon: FileCheck, perm: "applications.view" },
  { to: "/admin/sellers", label: "Sellers", icon: Store, perm: "sellers.view" },
  { section: "Catalogue" },
  { to: "/admin/official", label: "Official Store", icon: BadgeCheck, perm: "official.view" },
  { to: "/admin/products", label: "Products", icon: Package, perm: "products.view" },
  { to: "/admin/categories", label: "Categories", icon: Tags, perm: "categories.view" },
  { to: "/admin/reviews", label: "Reviews", icon: MessageSquare, perm: "reviews.view" },
  { section: "Sales" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, perm: "orders.view" },
  { to: "/admin/disputes", label: "Disputes", icon: AlertOctagon, perm: "disputes.view" },
  { section: "Finance" },
  { to: "/admin/finances", label: "Finance", icon: Landmark, perm: "finance.view" },
  { to: "/admin/crypto", label: "Crypto Payments", icon: Bitcoin, perm: "crypto.view" },
  { to: "/admin/withdrawals", label: "Payouts", icon: Wallet, perm: "payouts.view" },
  { section: "Marketing" },
  { to: "/admin/discounts", label: "Discount Codes", icon: TicketPercent, perm: "discounts.view" },
  { to: "/admin/promotions", label: "Promotions", icon: Megaphone, perm: "promotions.view" },
  { section: "System" },
  { to: "/admin/settings", label: "Settings", icon: SlidersHorizontal, perm: "settings.view" },
  { to: "/admin/webhooks", label: "Discord Webhooks", icon: Webhook, adminOnly: true },
  { to: "/admin/staff-accounts", label: "Staff Accounts", icon: UserCog, adminOnly: true },
  { to: "/admin/staff-roles", label: "Staff Roles", icon: ShieldCheck, adminOnly: true },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText, perm: "audit.view" },
];

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/product/:id" element={<ProductPage />} />
              <Route path="/store/:slug" element={<StorePage />} />
              <Route path="/promo/:slug" element={<PromoPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/payment/crypto/:orderId" element={<CryptoPaymentPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/staff/login" element={<StaffLoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/become-a-seller" element={<BecomeSellerPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/legal/:doc" element={<LegalPage />} />
            </Route>
            <Route path="/account" element={<RequireAuth><DashboardLayout title="Account" nav={ACCOUNT_NAV} testId="account-layout" /></RequireAuth>}>
              <Route index element={<A.AccountOverview />} />
              <Route path="orders" element={<A.AccountOrders />} />
              <Route path="orders/:id" element={<A.AccountOrderDetail />} />
              <Route path="wishlist" element={<A.AccountWishlist />} />
              <Route path="reviews" element={<A.AccountReviews />} />
              <Route path="notifications" element={<A.AccountNotifications />} />
              <Route path="settings" element={<A.AccountSettings />} />
              <Route path="linked" element={<A.AccountLinked />} />
            </Route>
            <Route path="/seller" element={<RequireAuth roles={["SELLER"]}><DashboardLayout title="Seller Panel" nav={SELLER_NAV} testId="seller-layout" variant="staff" /></RequireAuth>}>
              <Route index element={<S.SellerDashboard />} />
              <Route path="products" element={<S.SellerProducts />} />
              <Route path="products/:id" element={<S.SellerProductEditor />} />
              <Route path="orders" element={<S.SellerOrders />} />
              <Route path="earnings" element={<S.SellerEarnings />} />
              <Route path="withdrawals" element={<S.SellerWithdrawals />} />
              <Route path="reviews" element={<S.SellerReviews />} />
              <Route path="store" element={<S.SellerStore />} />
            </Route>
            <Route path="/admin" element={<RequireAuth staff><DashboardLayout title="Staff Panel" nav={ADMIN_NAV} testId="admin-layout" variant="staff" /></RequireAuth>}>
              <Route index element={<AD.AdminDashboard />} />
              <Route path="users" element={<AD.AdminUsers />} />
              <Route path="applications" element={<AD.AdminApplications />} />
              <Route path="sellers" element={<AD.AdminSellers />} />
              <Route path="products" element={<AD.AdminProducts />} />
              <Route path="categories" element={<AD.AdminCategories />} />
              <Route path="orders" element={<AD.AdminOrders />} />
              <Route path="disputes" element={<AD.AdminDisputes />} />
              <Route path="finances" element={<AD.AdminFinances />} />
              <Route path="withdrawals" element={<AD.AdminWithdrawals />} />
              <Route path="reviews" element={<AD.AdminReviews />} />
              <Route path="settings" element={<AD.AdminSettings />} />
              <Route path="audit" element={<AD.AdminAudit />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="crypto" element={<AdminCryptoPayments />} />
              <Route path="staff-roles" element={<AdminStaffRoles />} />
              <Route path="staff-accounts" element={<AdminStaffAccounts />} />
              <Route path="official" element={<AdminOfficialStore />} />
              <Route path="webhooks" element={<AdminWebhooks />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#18181B", border: "1px solid #27272A", color: "#fff" } }} />
      </CartProvider>
    </AuthProvider>
  );
}
